import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { locations, calculateEmployeePayroll } from '../data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DollarSign,
  Clock,
  TrendingUp,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const MyPayroll = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('weekly');

  const payrollData = useMemo(() => {
    if (!user?.id) return null;
    return calculateEmployeePayroll(user.id, period);
  }, [user, period]);

  const periodLabels = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    annual: 'This Year',
  };

  const location = locations.find((l) => l.id === user?.locationId);

  if (!payrollData) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">No Payroll Data</h2>
          <p className="text-gray-500 mt-2">Unable to load your payroll information.</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Estimated Earnings',
      value: `$${payrollData.totalPay.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-purple-500',
      description: periodLabels[period],
    },
    {
      title: 'Total Hours',
      value: payrollData.totalHours.toFixed(1),
      icon: Clock,
      color: 'bg-blue-500',
      description: 'Hours worked',
    },
    {
      title: 'Regular Hours',
      value: payrollData.regularHours.toFixed(1),
      icon: TrendingUp,
      color: 'bg-purple-500',
      description: `@ $${user?.hourlyRate?.toFixed(2) || '0.00'}/hr`,
    },
    {
      title: 'Overtime Hours',
      value: payrollData.overtimeHours.toFixed(1),
      icon: AlertCircle,
      color: payrollData.overtimeHours > 0 ? 'bg-amber-500' : 'bg-gray-400',
      description: payrollData.overtimeHours > 0 ? '1.5x rate applied' : 'No overtime',
    },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Payroll</h1>
        <p className="text-gray-500 mt-1">
          View your earnings at {location?.name || 'your location'}
        </p>
      </div>

      {/* Period Tabs */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Tabs value={period} onValueChange={setPeriod}>
              <TabsList className="grid grid-cols-4 w-full max-w-md">
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="annual">Annual</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Employee ID:</span>
              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                {user?.employeeNumber}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Showing data from {format(parseISO(payrollData.startDate), 'MMM d, yyyy')} to{' '}
            {format(parseISO(payrollData.endDate), 'MMM d, yyyy')}
          </p>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-sm text-gray-400 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Earnings Breakdown */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Earnings Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Regular Pay</p>
                <p className="text-sm text-gray-500">
                  {payrollData.regularHours.toFixed(2)} hours @ ${user?.hourlyRate?.toFixed(2) || '0.00'}/hr
                </p>
              </div>
              <p className="font-semibold text-gray-900">
                ${(payrollData.regularHours * (user?.hourlyRate || 0)).toFixed(2)}
              </p>
            </div>
            
            {payrollData.overtimeHours > 0 && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Overtime Pay</p>
                  <p className="text-sm text-gray-500">
                    {payrollData.overtimeHours.toFixed(2)} hours @ ${((user?.hourlyRate || 0) * 1.5).toFixed(2)}/hr (1.5x)
                  </p>
                </div>
                <p className="font-semibold text-amber-600">
                  ${(payrollData.overtimeHours * (user?.hourlyRate || 0) * 1.5).toFixed(2)}
                </p>
              </div>
            )}
            
            <div className="flex justify-between items-center py-3 bg-purple-50 -mx-6 px-6 rounded-b-lg">
              <p className="font-semibold text-gray-900">Total Estimated Earnings</p>
              <p className="text-2xl font-bold text-purple-600">
                ${payrollData.totalPay.toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Entries for Period */}
      {payrollData.entries.length > 0 && (
        <Card className="mt-6 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              Time Entries ({payrollData.entries.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="font-medium">Clock In</TableHead>
                  <TableHead className="font-medium">Clock Out</TableHead>
                  <TableHead className="font-medium text-right">Hours</TableHead>
                  <TableHead className="font-medium text-right">Earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollData.entries.map((entry) => (
                  <TableRow key={entry.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {format(parseISO(entry.date), 'EEE, MMM d')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {format(parseISO(entry.clockIn), 'h:mm a')}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {entry.clockOut
                        ? format(parseISO(entry.clockOut), 'h:mm a')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium text-gray-900">
                      {entry.hoursWorked.toFixed(2)}h
                    </TableCell>
                    <TableCell className="text-right font-medium text-purple-600">
                      ${(entry.hoursWorked * (user?.hourlyRate || 0)).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Notice */}
      <Card className="mt-6 border-0 shadow-md bg-blue-50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> These are estimated earnings before taxes and deductions. 
            Actual pay may vary. For questions about your payroll, please contact your manager.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPayroll;
